import { useEffect, useRef, useState } from "react";

import { accountColor, accountInitial, accountName } from "../lib/accounts.js";
import type { AccountInfo, UnreadByAccount } from "../lib/types.js";
import { IconLogout, IconPhone, IconSettings } from "./icons.js";

/**
 * Accountknop rechtsboven, zoals Outlook: een cirkel met een letter, met
 * daaronder een uitklapmenu. Dit verving drie losse knoppen plus het
 * uitgeschreven adres in de bovenbalk — samen ~370px die op een telefoon het
 * logo van het scherm drukten.
 *
 * Met meerdere mailboxen is dit ook de wisselaar. Elke mailbox heeft een eigen
 * kleur en de bedrijfsletter in het rondje, en een stip op de knop verraadt
 * ongelezen post in een mailbox die je nu niet bekijkt.
 */

interface Props {
  accounts: AccountInfo[];
  active: AccountInfo;
  unread: UnreadByAccount;
  onSwitch: (id: string) => void;
  onAddAccount: () => void;
  onRemoveAccount: (account: AccountInfo) => void;
  onDeviceSetup: () => void;
  onSettings: () => void;
  onLogout: () => void;
}

const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]';

function Avatar({ accounts, account }: { accounts: AccountInfo[]; account: AccountInfo }): React.JSX.Element {
  const multiple = accounts.length > 1;
  return (
    <span
      className="avatar"
      aria-hidden="true"
      style={multiple ? { background: accountColor(accounts, account.id), color: "#fff" } : undefined}
    >
      {accountInitial(account.address, multiple)}
    </span>
  );
}

function formatCount(n: number): string {
  return n > 999 ? "999+" : String(n);
}

export function AccountMenu({
  accounts,
  active,
  unread,
  onSwitch,
  onAddAccount,
  onRemoveAccount,
  onDeviceSetup,
  onSettings,
  onLogout,
}: Props): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const multiple = accounts.length > 1;
  const elders = accounts
    .filter((a) => a.id !== active.id)
    .reduce((sum, a) => sum + (unread[a.id] ?? 0), 0);

  // Sluiten bij klik/tik buiten knop én menu. De listener bestaat alleen
  // zolang het menu open is; permanent luisteren op document is verspilling.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: PointerEvent): void {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      setOpen(false);
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        setOpen(false);
        // Focus terug naar de knop, anders belandt de toetsenbordgebruiker
        // in het niets en moet hij opnieuw naar de balk tabben.
        buttonRef.current?.focus();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const items = menuRef.current?.querySelectorAll<HTMLButtonElement>(ITEM_SELECTOR);
        if (!items || items.length === 0) return;
        const list = Array.from(items);
        const index = list.indexOf(document.activeElement as HTMLButtonElement);
        const next =
          e.key === "ArrowDown"
            ? list[(index + 1) % list.length]
            : list[(index - 1 + list.length) % list.length];
        next?.focus();
      }
      // Tab laten we met rust: de browser verlaat het menu dan vanzelf, en de
      // pointerdown-listener ruimt de open-status op bij de volgende klik.
      if (e.key === "Tab") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Bij openen meteen het eerste item focussen, zodat pijltjes en Enter
  // direct werken zonder eerst te hoeven tabben.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLButtonElement>(ITEM_SELECTOR);
    first?.focus();
  }, [open]);

  function kies(actie: () => void): void {
    setOpen(false);
    actie();
  }

  return (
    <div className="account">
      {multiple && (
        // Op brede schermen staat er naast het rondje voluit in welke mailbox
        // je zit; op een telefoon is daar geen plek en doen kleur en letter het.
        <span className="account-current" aria-hidden="true">{active.address}</span>
      )}
      <button
        ref={buttonRef}
        type="button"
        className="avatar-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={
          elders > 0
            ? `Account: ${active.address}. ${elders} ongelezen in andere mailboxen`
            : `Account: ${active.address}`
        }
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar accounts={accounts} account={active} />
        {elders > 0 && <span className="avatar-dot" aria-hidden="true" />}
      </button>

      {open && (
        <div ref={menuRef} className="account-menu" role="menu" aria-label="Account">
          {multiple ? (
            <div className="account-menu-group" role="group" aria-label="Mailboxen">
              {accounts.map((account) => {
                const count = unread[account.id] ?? 0;
                const current = account.id === active.id;
                return (
                  <button
                    key={account.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={current}
                    aria-label={`${accountName(account.address)}, ${account.address}${count > 0 ? `, ${count} ongelezen` : ""}`}
                    className={`account-menu-account${current ? " is-active" : ""}`}
                    onClick={() => kies(() => onSwitch(account.id))}
                  >
                    <Avatar accounts={accounts} account={account} />
                    <span className="account-menu-who">
                      <span className="account-menu-name">{accountName(account.address)}</span>
                      <span className="account-menu-address">{account.address}</span>
                    </span>
                    {count > 0 && (
                      <span className="folder-badge" aria-label={`${count} ongelezen`}>
                        {formatCount(count)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="account-menu-head">
              <Avatar accounts={accounts} account={active} />
              <span className="account-menu-address">{active.address}</span>
            </div>
          )}

          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onAddAccount)}>
            <span className="account-menu-plus" aria-hidden="true">+</span>
            Mailbox toevoegen
          </button>

          <div className="account-menu-sep" role="separator" />

          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onDeviceSetup)}>
            <IconPhone size={18} />
            Op mijn telefoon
          </button>
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onSettings)}>
            <IconSettings size={18} />
            Instellingen
          </button>

          <div className="account-menu-sep" role="separator" />

          {multiple && (
            <button
              type="button"
              role="menuitem"
              className="account-menu-item"
              onClick={() => kies(() => onRemoveAccount(active))}
            >
              <IconLogout size={18} />
              {accountName(active.address)} afmelden
            </button>
          )}
          <button type="button" role="menuitem" className="account-menu-item" onClick={() => kies(onLogout)}>
            <IconLogout size={18} />
            {multiple ? "Uitloggen uit alle mailboxen" : "Uitloggen"}
          </button>
        </div>
      )}
    </div>
  );
}
